/**
 * aideySessionsService.js
 * Persists Aidey chat sessions to localStorage (runtime) and DB (durable).
 * Each session: { id, title, messages[], createdAt, updatedAt }
 */
import { api } from '../api/client';

const KEY = 'aidey_sessions';
const MAX_SESSIONS = 200;
const MAX_MESSAGES_PER_SESSION = 150;

export function loadSessions() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
}

export function loadSession(id) {
  if (!id) return null;
  return loadSessions().find(s => s.id === id) || null;
}

export function saveSession(session) {
  const all = loadSessions();
  const idx = all.findIndex(s => s.id === session.id);
  const updated = { ...session, updatedAt: new Date().toISOString() };
  if (idx >= 0) all[idx] = updated;
  else all.unshift(updated);
  localStorage.setItem(KEY, JSON.stringify(all.slice(0, MAX_SESSIONS)));
  window.dispatchEvent(new Event('aidey:onboardingProgress'));
  return updated;
}

export function deleteSession(id) {
  const remaining = loadSessions().filter(s => s.id !== id);
  localStorage.setItem(KEY, JSON.stringify(remaining));
}

export function deleteAllSessions() {
  localStorage.removeItem(KEY);
}

/** Generate a stable unique ID for a new session. */
export function newSessionId() {
  return `aidey_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Persist chat messages into a session (localStorage only).
 * Only saves if there's at least one user message (ignores welcome-only sessions).
 * Auto-derives title from the first user message.
 */
export function persistMessages(sessionId, messages) {
  if (!sessionId) return;
  const userMsg = messages.find(m => m.role === 'user');
  if (!userMsg) return; // nothing worth saving

  const existing = loadSession(sessionId);
  const title = userMsg.content.slice(0, 60) + (userMsg.content.length > 60 ? '…' : '');

  saveSession({
    id: sessionId,
    title: existing?.title || title,
    createdAt: existing?.createdAt || new Date().toISOString(),
    messages: messages.slice(-MAX_MESSAGES_PER_SESSION),
  });
}

// ── DB-backed operations ───────────────────────────────────────────────────

/** Generate a short AI title for a session via the backend. */
export async function generateSessionTitle(firstMessage) {
  try {
    const res = await api('/api/aidey-sessions/title', {
      method: 'POST',
      body: JSON.stringify({ firstMessage }),
    });
    if (!res.ok) throw new Error();
    const data = await res.json();
    return data.title || firstMessage.slice(0, 50);
  } catch {
    return firstMessage.slice(0, 50);
  }
}

/** Save/update a session to the database. Fire-and-forget safe. */
export async function saveSessionToDb(session) {
  if (!session?.id) return;
  try {
    await api('/api/aidey-sessions/save', {
      method: 'POST',
      body: JSON.stringify({ id: session.id, title: session.title, messages: session.messages, projectId: session.projectId }),
    });
  } catch { /* non-fatal */ }
}

/** Load the most recent session from DB (within last 24h). Returns null if none. */
export async function getLastDbSession() {
  try {
    const res = await api('/api/aidey-sessions/last');
    if (!res.ok) return null;
    const data = await res.json();
    return data.session || null;
  } catch { return null; }
}

/** Load full session (including messages) from DB by id. */
export async function loadSessionFromDb(id) {
  try {
    const res = await api(`/api/aidey-sessions/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.session || null;
  } catch { return null; }
}

/** Fetch all sessions from DB (metadata only). Returns [] on error. */
export async function loadSessionsFromDb() {
  try {
    const res = await api('/api/aidey-sessions/');
    if (!res.ok) return [];
    const data = await res.json();
    return data.sessions || [];
  } catch { return []; }
}

/** Delete a session from DB. */
export async function deleteSessionFromDb(id) {
  try {
    await api(`/api/aidey-sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch { /* non-fatal */ }
}

/** Rename a session in DB. */
export async function renameSessionInDb(id, title) {
  try {
    await api(`/api/aidey-sessions/${encodeURIComponent(id)}/title`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  } catch { /* non-fatal */ }
}

/** Clear all sessions from DB. */
export async function deleteAllSessionsFromDb() {
  try {
    await api('/api/aidey-sessions/', { method: 'DELETE' });
  } catch { /* non-fatal */ }
}
